import React, { createContext, useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";

export const ConfirmDialogContext = createContext(null);

const CLOSED = {
  open: false,
  title: "",
  description: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  destructive: false,
  icon: null,
  loading: false,
  onConfirm: null,
};

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(CLOSED);
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ ...CLOSED, ...options, open: true, loading: false });
    });
  }, []);

  const close = useCallback((result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState(CLOSED);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (state.loading) return;

    if (typeof state.onConfirm === "function") {
      setState((s) => ({ ...s, loading: true }));
      try {
        await state.onConfirm();
        close(true);
      } catch {
        close(false);
      }
    } else {
      close(true);
    }
  }, [state, close]);

  const handleCancel = useCallback(() => {
    if (state.loading) return;
    close(false);
  }, [state.loading, close]);

  const accentColor = state.destructive
    ? "#EA001B"
    : "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)";

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}

      <Dialog
        open={state.open}
        onClose={handleCancel}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 180 }}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        disableEscapeKeyDown={state.loading}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "24px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)",
            maxWidth: 400,
            width: "calc(100% - 32px)",
            m: 2,
          },
        }}
      >
        {/* Icon */}
        {state.icon && (
          <Box sx={{ pt: 3, px: 3, display: "flex", justifyContent: "center" }}>
            {state.icon}
          </Box>
        )}

        {/* Title */}
        <Box sx={{ px: 3, pt: state.icon ? 1.5 : 3, pb: state.description ? 0.5 : 1 }}>
          <Typography
            id="confirm-dialog-title"
            sx={{ fontWeight: 700, fontSize: "1rem", color: "#362B24", fontFamily: "Heebo" }}
          >
            {state.title || "Are you sure?"}
          </Typography>
        </Box>

        {/* Description */}
        {state.description && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography
              id="confirm-dialog-description"
              variant="body2"
              sx={{ color: "#6B7280", fontFamily: "Heebo", lineHeight: 1.65 }}
            >
              {state.description}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 3,
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleCancel}
            disabled={state.loading}
            disableElevation
            sx={{
              borderRadius: "28px",
              height: 40,
              px: 3,
              border: "1px solid #E5E7EB",
              color: "#362B24",
              fontFamily: "Heebo",
              fontWeight: 500,
              fontSize: "0.875rem",
              textTransform: "none",
              background: "transparent",
              "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
              "&.Mui-disabled": { opacity: 0.45 },
            }}
          >
            {state.cancelText || "Cancel"}
          </Button>

          <Button
            onClick={handleConfirm}
            disabled={state.loading}
            disableElevation
            aria-label={state.confirmText || "Confirm"}
            sx={{
              borderRadius: "28px",
              height: 40,
              px: 3,
              minWidth: 96,
              background: accentColor,
              color: "#fff",
              fontFamily: "Heebo",
              fontWeight: 600,
              fontSize: "0.875rem",
              textTransform: "none",
              boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
              "&:hover": { opacity: 0.88, background: accentColor },
              "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF", boxShadow: "none" },
            }}
          >
            {state.loading ? (
              <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
            ) : (
              state.confirmText || "Confirm"
            )}
          </Button>
        </Box>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}
